import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SignInPopup.css';

/**
 * SignInPopup - A popup modal that encourages non-authenticated users to sign in
 * 
 * Features displayed:
 * - Add ingredients you have at home to view recipes better
 * - Cost multiple recipes at once
 * 
 * @param {boolean} show - Controls visibility of the popup
 * @param {function} onClose - Callback when popup is closed
 * @param {string} [subtitle] - Optional subtitle (e.g. "You need to sign in to continue.")
 */
const SignInPopup = ({ show, onClose, subtitle }) => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (show) {
            // Add a slight delay for animation
            setTimeout(() => setIsVisible(true), 10);
            // Prevent body scroll on mobile
            document.body.classList.add('popup-open');
        } else {
            setIsVisible(false);
            // Re-enable body scroll
            document.body.classList.remove('popup-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('popup-open');
        };
    }, [show]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onClose(), 300); // Wait for animation to finish
    };

    const handleSignIn = () => {
        handleClose();
        navigate('/auth/register');
    };

    const handleDismiss = () => {
        handleClose();
    };

    if (!show) return null;

    return (
        <div className={`signin-popup-overlay ${isVisible ? 'visible' : ''}`}>
            <div 
                className={`signin-popup-content ${isVisible ? 'visible' : ''}`} 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top right: Dismiss popup */}
                <button 
                    type="button"
                    className="signin-popup-top-right-btn"
                    onClick={handleDismiss}
                >
                    Close
                </button>

                {/* Header */}
                <div className="signin-popup-header">
                    <div className="signin-popup-icon">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                        </svg>
                    </div>
                    <h2 className="signin-popup-title">Sign in - It's Free!</h2>
                    <p className="signin-popup-subtitle">{subtitle || 'Unlock powerful features to enhance your cooking experience'}</p>
                </div>

                {/* Features List */}
                <div className="signin-popup-features">
                    <div className="signin-popup-feature">
                        <div className="feature-icon">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                            </svg>
                        </div>
                        <div className="feature-content">
                            <h3 className="feature-title">Add Your Ingredients</h3>
                            <p className="feature-description">Keep track of ingredients you have at home and find recipes that match what's already in your cupboard</p>
                        </div>
                    </div>

                    <div className="signin-popup-feature">
                        <div className="feature-icon">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="feature-content">
                            <h3 className="feature-title">Cost Multiple Recipes</h3>
                            <p className="feature-description">Calculate costs for all your recipes at once and plan your meals within budget</p>
                        </div>
                    </div>

                    <div className="signin-popup-feature">
                        <div className="feature-icon">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                        </div>
                        <div className="feature-content">
                            <h3 className="feature-title">Save Your Favorites</h3>
                            <p className="feature-description">Create your personal recipe collection and access it anytime</p>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="signin-popup-actions">
                    <button 
                        className="signin-popup-btn signin-popup-btn-primary"
                        onClick={handleSignIn}
                    >
                        Sign In Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignInPopup;
